import type { FieldType } from '#types/System.types';
import { COMPOSITE_FIELD_EMPTY_PLACEHOLDER } from './compositeFieldPlaceholders';
import { appendShippingTrackingUrlToFormattedValueParts } from './appendShippingTrackingUrlToFormattedValueParts';
import { formattedCellValueToString, type FormattedCellValue } from './formattedCellValue';

export type FormattedValueParts = {
  prefix?: string;
  formatted: FormattedCellValue;
  suffix?: string;
};

export function formatFieldValueParts(value: unknown, fieldType: FieldType | undefined): FormattedValueParts {
  if (!fieldType) return { formatted: stringifyValue(value) };

  let result: FormattedValueParts;
  const options = fieldType.props?.options;
  if (options?.length) {
    if (Array.isArray(value)) {
      result = { formatted: value.map((v) => options.find((o) => o.value === v)?.label ?? stringifyValue(v)).join(', ') };
    } else {
      result = { formatted: options.find((o) => o.value === value)?.label ?? stringifyValue(value) };
    }
  } else if (fieldType.formatter) {
    const formatted = fieldType.formatter(value);
    if (!formatted && value != null) {
      result = { prefix: fieldType.prefix, formatted: stringifyValue(value), suffix: fieldType.suffix };
    } else {
      result = { prefix: fieldType.prefix, formatted, suffix: fieldType.suffix };
    }
  } else {
    result = { formatted: stringifyValue(value) };
  }

  return appendShippingTrackingUrlToFormattedValueParts(result, value, fieldType);
}

function stringifyValue(value: unknown): string {
  if (value == null) return '';
  if (typeof value !== 'object') return String(value);
  const obj = value as Record<string, unknown>;
  if (typeof obj.formatted === 'string' && obj.formatted) return obj.formatted;
  return COMPOSITE_FIELD_EMPTY_PLACEHOLDER;
}

function formatPreviewValue(value: unknown, fieldType: FieldType | undefined): string {
  const { prefix, formatted, suffix } = formatFieldValueParts(value, fieldType);
  return (prefix ?? '') + formattedCellValueToString(formatted) + (suffix ?? '');
}

export default formatPreviewValue;
